import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ChevronRight, FileText, Shield, Info, ExternalLink, Calendar, RotateCcw, Trash2, AlertTriangle, Loader2, Home, Lock, Utensils, Sparkles } from "lucide-react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import { DISCLAIMER_FULL, PRIVACY_FULL } from "@/components/Disclaimer";
import { toast } from "sonner";
import api from "@/lib/api";
import { SubscriptionCard } from "@/components/settings/SubscriptionCard";
import { NotificationsSetupWizard } from "@/components/settings/NotificationsSetupWizard";
import { PushNotificationsCard } from "@/components/settings/PushNotificationsCard";
import { SoundsCard } from "@/components/settings/SoundsCard";
import { VoiceCheckInScheduleCard } from "@/components/settings/VoiceCheckInScheduleCard";
import { DataManagementCard } from "@/components/settings/DataManagementCard";
import { SecurityCard } from "@/components/settings/SecurityCard";
import { WipeEmaDataCard } from "@/components/settings/WipeEmaDataCard";
import { BiweeklyHistoryPanel } from "@/components/biweekly/BiweeklyAssessment";
import NativeDiagCard from "@/components/settings/NativeDiagCard";

const SettingsPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [showLegal, setShowLegal] = useState(false);
  const [showPrivacy, setShowPrivacy] = useState(false);
  const [showPeriodDialog, setShowPeriodDialog] = useState(false);
  const [showResetDialog, setShowResetDialog] = useState(false);
  const [resetSection, setResetSection] = useState(null);
  
  const [periodStartDate, setPeriodStartDate] = useState("");
  const [currentPeriodDate, setCurrentPeriodDate] = useState(null);
  const [loading, setLoading] = useState(false);
  
  // Subscription state
  const [subscription, setSubscription] = useState(null);
  const [subLoading, setSubLoading] = useState(true);

  // Current user (for Security card)
  const [currentUser, setCurrentUser] = useState(null);
  useEffect(() => {
    api.get("/auth/me").then((res) => setCurrentUser(res.data)).catch(() => {});
  }, []);
  
  // Home page preference
  const HOME_PAGE_PREF_KEY = "yourlifemap_home_page";

  const [homePage, setHomePage] = useState(() => {
    return localStorage.getItem(HOME_PAGE_PREF_KEY) || "/oggi";
  });
  
  const homePageOptions = [
    { value: "/oggi", label: "Oggi (Dashboard)", icon: "🏠" },
    { value: "/organizza", label: "Organizza", icon: "📋" },
    { value: "/organizza/tasks", label: "Life Tasks", icon: "✅" },
    { value: "/organizza/calendario", label: "Calendario", icon: "📅" },
    { value: "/organizza/spese", label: "Spese", icon: "💰" },
    { value: "/benessere", label: "Benessere", icon: "💚" },
    { value: "/benessere/umore", label: "Umore", icon: "😊" },
    { value: "/benessere/abitudini", label: "Abitudini", icon: "🎯" },
    { value: "/benessere/diario", label: "Diario", icon: "📔" },
    { value: "/benessere/note", label: "Note", icon: "📝" },
  ];
  
  const handleHomePageChange = (value) => {
    setHomePage(value);
    localStorage.setItem(HOME_PAGE_PREF_KEY, value);
    toast.success("Pagina iniziale aggiornata!");
  };

  // Load current settings
  useEffect(() => {
    const loadSettings = async () => {
      try {
        const response = await api.get(`/settings`);
        if (response.data.period_start_date) {
          setCurrentPeriodDate(response.data.period_start_date);
          setPeriodStartDate(response.data.period_start_date);
        }
      } catch (error) {
        if (process.env.NODE_ENV === 'development') {
          console.error("Error loading settings:", error);
        }
      }
    };
    loadSettings();
    loadSubscription();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Check for payment return
  useEffect(() => {
    const sessionId = searchParams.get('session_id');
    const subStatus = searchParams.get('subscription');
    const isPromo = searchParams.get('promo');
    
    if (sessionId && subStatus === 'success') {
      // Poll for payment status
      pollPaymentStatus(sessionId);
    } else if (subStatus === 'activated' && isPromo) {
      toast.success("Abbonamento attivato con codice promo!");
      loadSubscription();
    } else if (subStatus === 'cancelled') {
      toast.info("Pagamento annullato");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  const loadSubscription = async () => {
    setSubLoading(true);
    try {
      const response = await api.get('/stripe/subscription-status');
      setSubscription(response.data);
    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        console.error("Error loading subscription:", error);
      }
    } finally {
      setSubLoading(false);
    }
  };

  const pollPaymentStatus = async (sessionId, attempts = 0) => {
    const maxAttempts = 10;
    
    if (attempts >= maxAttempts) {
      toast.error("Timeout verifica pagamento. Controlla la tua email.");
      return;
    }
    
    try {
      const response = await api.get(`/stripe/checkout-status/${sessionId}`);
      
      if (response.data.payment_status === 'paid') {
        toast.success("Pagamento completato! Abbonamento attivato.");
        loadSubscription();
        // Clean URL
        window.history.replaceState({}, '', '/impostazioni');
        return;
      }
      
      // Continue polling
      setTimeout(() => pollPaymentStatus(sessionId, attempts + 1), 2000);
    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        console.error("Error checking payment:", error);
      }
    }
  };

  const handleSetPeriod = async () => {
    setLoading(true);
    try {
      await api.post(`/settings/period`, {
        start_date: periodStartDate || null
      });
      setCurrentPeriodDate(periodStartDate || null);
      setShowPeriodDialog(false);
      toast.success(periodStartDate ? "Nuovo periodo impostato" : "Periodo rimosso");
    } catch (error) {
      toast.error("Errore nel salvare il periodo");
    } finally {
      setLoading(false);
    }
  };

  const handleResetSection = async () => {
    if (!resetSection) return;
    
    setLoading(true);
    try {
      await api.post(`/settings/reset-section`, {
        section: resetSection.id
      });
      setShowResetDialog(false);
      setResetSection(null);
      toast.success(`${resetSection.name} resettato con successo`);
    } catch (error) {
      toast.error("Errore nel reset della sezione");
    } finally {
      setLoading(false);
    }
  };

  const sections = [
    { id: "expenses", name: "Spese", icon: "💰" },
    { id: "habits", name: "Abitudini", icon: "✓" },
    { id: "moods", name: "Umore", icon: "😊" },
    { id: "appointments", name: "Appuntamenti", icon: "📅" },
    { id: "diary", name: "Diario", icon: "📝" },
    { id: "needs", name: "Bisogni", icon: "🎯" },
    { id: "life_tasks", name: "Compiti", icon: "📋" },
    { id: "reminders", name: "Promemoria", icon: "🔔" },
  ];

  const formatDate = (dateStr) => {
    if (!dateStr) return null;
    const date = new Date(dateStr);
    return date.toLocaleDateString('it-IT', { day: 'numeric', month: 'long', year: 'numeric' });
  };

  return (
    <div className="space-y-6 animate-fade-in pb-8" data-testid="settings-page">
      {/* Header */}
      <div>
        <h1 className="page-title">Impostazioni</h1>
        <p className="page-subtitle">Gestisci il tuo account e le preferenze</p>
      </div>

      <div className="space-y-4 max-w-2xl">
        
        {/* Abbonamento Premium */}
        <SubscriptionCard subscription={subscription} subLoading={subLoading} onRefresh={loadSubscription} />

        {/* Pagina Iniziale */}
        <Card className="border border-border shadow-sm rounded-sm">
          <CardHeader className="pb-2">
            <CardTitle className="font-heading text-lg flex items-center gap-2">
              <Home className="w-5 h-5" strokeWidth={1.5} />
              Pagina Iniziale
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <p className="text-sm text-muted-foreground">
              Scegli quale pagina vedere quando entri nell'app.
            </p>
            
            <Select value={homePage} onValueChange={handleHomePageChange}>
              <SelectTrigger className="w-full rounded-sm" data-testid="home-page-select">
                <SelectValue placeholder="Seleziona pagina iniziale" />
              </SelectTrigger>
              <SelectContent>
                {homePageOptions.map((option) => (
                  <SelectItem key={option.value} value={option.value}>
                    <span className="flex items-center gap-2">
                      <span>{option.icon}</span>
                      <span>{option.label}</span>
                    </span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            
            <p className="text-xs text-muted-foreground">
              La prossima volta che entri nell'app, andrai direttamente a questa pagina.
            </p>
          </CardContent>
        </Card>
        
        {/* Wizard setup notifiche (3 step guidati) */}
        <NotificationsSetupWizard />

        {/* Notifiche Push */}
        <PushNotificationsCard />

        {/* Suoni interni app */}
        <SoundsCard />

        {/* Promemoria Check-in Vocale */}
        <VoiceCheckInScheduleCard />
        
        {/* Nuovo Periodo */}
        <Card className="border border-border shadow-sm rounded-sm">
          <CardHeader className="pb-2">
            <CardTitle className="font-heading text-lg flex items-center gap-2">
              <Calendar className="w-5 h-5" strokeWidth={1.5} />
              Periodo di Monitoraggio
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <p className="text-sm text-muted-foreground">
              Imposta una data di inizio per visualizzare solo i dati da quel giorno in poi. I dati precedenti restano salvati.
            </p>
            
            {currentPeriodDate && (
              <div className="p-3 bg-muted/50 rounded-sm">
                <p className="text-sm">
                  <span className="text-muted-foreground">Periodo attivo dal:</span>{" "}
                  <span className="font-medium">{formatDate(currentPeriodDate)}</span>
                </p>
              </div>
            )}
            
            <Button
              variant="outline"
              onClick={() => setShowPeriodDialog(true)}
              className="rounded-sm w-full sm:w-auto"
              data-testid="set-period-btn"
            >
              <Calendar className="w-4 h-4 mr-2" />
              {currentPeriodDate ? "Modifica Periodo" : "Imposta Nuovo Periodo"}
            </Button>
          </CardContent>
        </Card>

        {/* Reset per Sezione */}
        <Card className="border border-border shadow-sm rounded-sm">
          <CardHeader className="pb-2">
            <CardTitle className="font-heading text-lg flex items-center gap-2">
              <RotateCcw className="w-5 h-5" strokeWidth={1.5} />
              Reset Sezioni
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <p className="text-sm text-muted-foreground">
              Cancella tutti i dati di una sezione specifica per ricominciare da zero.
            </p>
            
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
              {sections.map((section) => (
                <Button
                  key={section.id}
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    setResetSection(section);
                    setShowResetDialog(true);
                  }}
                  className="rounded-sm justify-start"
                  data-testid={`reset-${section.id}-btn`}
                >
                  <span className="mr-2">{section.icon}</span>
                  {section.name}
                </Button>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Extra strumenti — moduli non-core spostati fuori dalla navigazione principale.
            2026-07-10: la nutrizione è stata rimossa dai focus tile e dall'hub Benessere
            per mantenere Revela focalizzata sul monitoraggio psicologico. Resta però
            disponibile qui per chi la vuole usare. */}
        <Card className="border border-border shadow-sm rounded-sm" data-testid="settings-extra-card">
          <CardHeader className="pb-2">
            <CardTitle className="font-heading text-lg flex items-center gap-2">
              <Sparkles className="w-5 h-5" strokeWidth={1.5} />
              Extra
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <p className="text-sm text-muted-foreground">
              Strumenti opzionali non centrali per il monitoraggio EMA. Rimangono disponibili qui
              per chi li vuole usare in autonomia.
            </p>
            <Link to="/benessere?section=nutrizione" data-testid="settings-extra-nutrition-link">
              <Button
                variant="outline"
                className="rounded-sm w-full justify-between"
              >
                <span className="flex items-center gap-2">
                  <Utensils className="w-4 h-4" strokeWidth={1.5} />
                  Nutrizione & Calorie
                </span>
                <ChevronRight className="w-4 h-4 text-muted-foreground" />
              </Button>
            </Link>
          </CardContent>
        </Card>

        {/* Info Legali */}
        <Card className="border border-border shadow-sm rounded-sm">
          <CardHeader className="pb-2">
            <CardTitle className="font-heading text-lg flex items-center gap-2">
              <FileText className="w-5 h-5" strokeWidth={1.5} />
              Informazioni Legali
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            <button
              onClick={() => setShowLegal(true)}
              className="w-full flex items-center justify-between p-3 rounded-sm hover:bg-muted transition-colors text-left"
              data-testid="legal-info-btn"
            >
              <div className="flex items-center gap-3">
                <Shield className="w-4 h-4 text-muted-foreground" />
                <span className="text-sm">Disclaimer</span>
              </div>
              <ChevronRight className="w-4 h-4 text-muted-foreground" />
            </button>

            <button
              onClick={() => setShowPrivacy(true)}
              className="w-full flex items-center justify-between p-3 rounded-sm hover:bg-muted transition-colors text-left"
              data-testid="privacy-info-btn"
            >
              <div className="flex items-center gap-3">
                <Shield className="w-4 h-4 text-muted-foreground" />
                <span className="text-sm">Informativa Privacy</span>
              </div>
              <ChevronRight className="w-4 h-4 text-muted-foreground" />
            </button>

            <a
              href="/privacy-beta"
              className="w-full flex items-center justify-between p-3 rounded-sm hover:bg-muted transition-colors"
              data-testid="privacy-beta-link"
            >
              <div className="flex items-center gap-3">
                <Shield className="w-4 h-4 text-muted-foreground" />
                <span className="text-sm">Informativa Beta (GDPR + CNOP)</span>
              </div>
              <ChevronRight className="w-4 h-4 text-muted-foreground" />
            </a>

            <button
              onClick={async () => {
                try {
                  await api.post('/auth/reset-onboarding');
                  toast.success("Tutorial reimpostato. Verrà mostrato al prossimo accesso.");
                } catch {
                  toast.error("Errore. Riprova.");
                }
              }}
              className="w-full flex items-center justify-between p-3 rounded-sm hover:bg-muted transition-colors text-left"
              data-testid="reset-welcome-btn"
            >
              <div className="flex items-center gap-3">
                <Shield className="w-4 h-4 text-muted-foreground" />
                <span className="text-sm">Rivedi messaggio di benvenuto</span>
              </div>
              <ChevronRight className="w-4 h-4 text-muted-foreground" />
            </button>
            
            <a
              href="https://www.psicoapplicata.org"
              target="_blank"
              rel="noopener noreferrer"
              className="w-full flex items-center justify-between p-3 rounded-sm hover:bg-muted transition-colors"
            >
              <div className="flex items-center gap-3">
                <ExternalLink className="w-4 h-4 text-muted-foreground" />
                <span className="text-sm">Laboratorio di Psicologia Applicata</span>
              </div>
              <ChevronRight className="w-4 h-4 text-muted-foreground" />
            </a>

            <a
              href="https://www.laurapiccinini.it"
              target="_blank"
              rel="noopener noreferrer"
              className="w-full flex items-center justify-between p-3 rounded-sm hover:bg-muted transition-colors"
            >
              <div className="flex items-center gap-3">
                <ExternalLink className="w-4 h-4 text-muted-foreground" />
                <span className="text-sm">Laura Piccinini</span>
              </div>
              <ChevronRight className="w-4 h-4 text-muted-foreground" />
            </a>
          </CardContent>
        </Card>

        {/* I Miei Dati */}
        <DataManagementCard onLogout={() => { navigate('/login'); window.location.reload(); }} />

        {/* Biweekly assessment history + comparisons */}
        <BiweeklyHistoryPanel />

        {/* Cancella dati Experience Tracking (whitelist EMA only) */}
        <WipeEmaDataCard />

        {/* Sicurezza — cambio password */}
        <SecurityCard user={currentUser} />

        {/* Debug diagnostico modalità + push (temporaneo) */}
        <NativeDiagCard />

        {/* Info App */}
        <Card className="border border-border shadow-sm rounded-sm">
          <CardHeader className="pb-2">
            <CardTitle className="font-heading text-lg flex items-center gap-2">
              <Info className="w-5 h-5" strokeWidth={1.5} />
              Informazioni App
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3 text-sm">
              <div className="flex justify-between">
                <span className="text-muted-foreground">Versione</span>
                <span className="font-mono">1.1.0</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Ideato da</span>
                <span>Laura Piccinini</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Associazione</span>
                <span>Laboratorio di Psicologia Applicata</span>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Altro */}
        <Card className="border border-border shadow-sm rounded-sm">
          <CardHeader className="pb-2">
            <CardTitle className="font-heading text-lg">Altro</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                localStorage.removeItem("life_planner_onboarding_completed");
                window.location.reload();
              }}
              className="rounded-sm"
              data-testid="reset-onboarding-btn"
            >
              Mostra di nuovo l'introduzione
            </Button>

            {/* Admin Link */}
            <div className="pt-4 border-t border-border mt-4 space-y-2">
              <Link to="/admin">
                <Button variant="outline" size="sm" className="rounded-sm w-full justify-start">
                  <Lock className="w-4 h-4 mr-2" />
                  Console Admin
                </Button>
              </Link>
              <Link to="/clinico/documentazione">
                <Button
                  variant="outline"
                  size="sm"
                  className="rounded-sm w-full justify-start"
                  data-testid="settings-clinical-doc-link"
                >
                  <FileText className="w-4 h-4 mr-2" />
                  Documentazione tecnico-clinica
                </Button>
              </Link>
              <p className="text-xs text-muted-foreground">
                Accessibile solo agli amministratori
              </p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Period Dialog */}
      <Dialog open={showPeriodDialog} onOpenChange={setShowPeriodDialog}>
        <DialogContent className="max-w-md rounded-sm">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl">Imposta Periodo</DialogTitle>
            <DialogDescription>
              I dati inseriti prima di questa data non verranno mostrati nei tracker, ma resteranno salvati nel database.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div>
              <label className="text-sm font-medium mb-2 block">Data di inizio</label>
              <Input
                type="date"
                value={periodStartDate}
                onChange={(e) => setPeriodStartDate(e.target.value)}
                className="rounded-sm"
                data-testid="period-date-input"
              />
            </div>
          </div>
          
          <DialogFooter className="flex-col sm:flex-row gap-2">
            {currentPeriodDate && (
              <Button
                variant="outline"
                onClick={() => {
                  setPeriodStartDate("");
                  handleSetPeriod();
                }}
                className="rounded-sm"
                disabled={loading}
              >
                Rimuovi Periodo
              </Button>
            )}
            <Button
              onClick={handleSetPeriod}
              className="rounded-sm"
              disabled={loading || !periodStartDate}
              data-testid="confirm-period-btn"
            >
              {loading ? "Salvataggio..." : "Conferma"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reset Confirmation Dialog */}
      <Dialog open={showResetDialog} onOpenChange={setShowResetDialog}>
        <DialogContent className="max-w-md rounded-sm">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-destructive" />
              Conferma Reset
            </DialogTitle>
            <DialogDescription>
              Stai per cancellare tutti i dati della sezione <strong>{resetSection?.name}</strong>. Questa azione non può essere annullata.
            </DialogDescription>
          </DialogHeader>
          
          <DialogFooter className="flex-col sm:flex-row gap-2">
            <Button
              variant="outline"
              onClick={() => {
                setShowResetDialog(false);
                setResetSection(null);
              }}
              className="rounded-sm"
            >
              Annulla
            </Button>
            <Button
              variant="destructive"
              onClick={handleResetSection}
              className="rounded-sm"
              disabled={loading}
              data-testid="confirm-reset-btn"
            >
              <Trash2 className="w-4 h-4 mr-2" />
              {loading ? "Eliminazione..." : "Elimina Tutto"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Legal Dialog */}
      <Dialog open={showLegal} onOpenChange={setShowLegal}>
        <DialogContent className="max-w-lg rounded-sm">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl">Disclaimer</DialogTitle>
          </DialogHeader>
          <div className="text-sm leading-relaxed whitespace-pre-line text-muted-foreground max-h-80 overflow-y-auto">
            {DISCLAIMER_FULL}
          </div>
          <Button onClick={() => setShowLegal(false)} className="rounded-sm">
            Chiudi
          </Button>
        </DialogContent>
      </Dialog>

      {/* Privacy Dialog */}
      <Dialog open={showPrivacy} onOpenChange={setShowPrivacy}>
        <DialogContent className="max-w-lg rounded-sm">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl">Informativa Privacy</DialogTitle>
          </DialogHeader>
          <div className="text-sm leading-relaxed whitespace-pre-line text-muted-foreground max-h-80 overflow-y-auto">
            {PRIVACY_FULL}
          </div>
          <Button onClick={() => setShowPrivacy(false)} className="rounded-sm">
            Chiudi
          </Button>
        </DialogContent>
      </Dialog>

    </div>
  );
};

export default SettingsPage;
